import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Stack,
  Switch,
  Box,
  Skeleton,
} from "@mui/material";
import { momentLocalizer } from "react-big-calendar";
import moment from "moment";
import PropTypes from "prop-types";
import InterviewCalendar from "components/InterviewCalendar";
import { getMeetingDataByInterviewer } from "services/api";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const localizer = momentLocalizer(moment);

const MeetingsDialogWrapper = ({
  openMeetingsDialog,
  handleCloseMeetingsDialog,
  adminOrCeo,
  employee,
}) => {
  const [meetingsLoading, setMeetingsLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const { t } = useTranslation();

  // 1. Store ALL fetched data here (The "Master" list)
  const [allFetchedEvents, setAllFetchedEvents] = useState([]);

  // 2. Store Displayed data here (The "Filtered" list)
  const [visibleEvents, setVisibleEvents] = useState([]);

  // STATE: Controls whether to show All (Mode 2) or Mine (Mode 1)
  // Default: If admin, show all (true). If not, show mine (false).
  const [showAllEvents, setShowAllEvents] = useState(false);

  useEffect(() => {
    const fetchMeetingData = async () => {
      if (!openMeetingsDialog) return;

      setMeetingsLoading(true);
      try {
        // LOGIC:
        // If user is Admin AND switch is ON -> Mode 2 (All)
        // Otherwise (Not admin OR switch is OFF) -> Mode 1 (Mine)
        const mode = adminOrCeo ? 2 : 1;
        const employeedLoggedId = employee.id;

        const meetingsData = await getMeetingDataByInterviewer(employeedLoggedId, mode);
        console.log("Meetings Data::",meetingsData);

        if (meetingsData && meetingsData !== "error") {
          const allEvents = Object.values(meetingsData).flatMap((meetingArray) =>
            meetingArray.map((meeting) => {
              const startDate = new Date(meeting.meetingDate);
              const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

              return {
                id: meeting.id || Math.random(),
                title: `${t("admin.talentAcquisition.meetings.interview")}: ${
                  meeting.applicantName || t("common.candidate")
                }`,
                start: startDate,
                end: endDate,
                resource: meeting,
                allDay: false,
                interviewerId: meeting.interviewerId,
              };
            })
          );
          setAllFetchedEvents(allEvents);
        } else {
          setAllFetchedEvents([]);
        }
      } catch (error) {
        console.error("Failed to fetch meetings:", error);
        setAllFetchedEvents([]);
      } finally {
        setMeetingsLoading(false);
      }
    };

    fetchMeetingData();
    // Re-fetch when the Switch toggles (showAllEvents changes)
  }, [openMeetingsDialog, adminOrCeo, employee.id]);

  // --- 2. FILTER EFFECT (Runs when Switch Toggles) ---
  useEffect(() => {
    if (showAllEvents) {
      // Show Everything
      setVisibleEvents(allFetchedEvents);
    } else {
      // Filter: Show only where interviewerId matches logged in employee
      // Note: Data is inside 'ev.resource'
      const filtered = allFetchedEvents.filter((ev) => {
        // using == for loose equality (string vs number safe)
        return ev.interviewerId == employee.id;
      });
      setVisibleEvents(filtered);
    }
  }, [showAllEvents]);

  return (
    <Dialog
      open={openMeetingsDialog}
      onClose={handleCloseMeetingsDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: "90vh", display: "flex", flexDirection: "column" },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" component="div">
          {t("admin.talentAcquisition.meetings.scheduledInterviews")}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleCloseMeetingsDialog}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {meetingsLoading ? (
        /* --- SKELETON LOADING STATE --- */
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
          <Skeleton
            variant="rounded"
            height={50}
            width="100%"
            sx={{ borderRadius: 3, Margin: 10 }}
          />

          {/* 2. Skeleton for Calendar Header (Nav buttons & Title) */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Skeleton variant="rounded" width={120} height={40} /> {/* Prev/Next/Today */}
            <Skeleton variant="text" width={200} height={40} /> {/* Date Label */}
            <Skeleton variant="rounded" width={80} height={40} /> {/* Month/Week/Day */}
          </Box>

          {/* 3. Skeleton for Calendar Grid */}
          <Skeleton variant="rectangular" sx={{ flex: 1, borderRadius: 2 }} animation="wave" />
        </Box>
      ) : (
        <DialogContent
          dividers
          sx={{ p: { xs: 1, sm: 2 }, flex: 1, display: "flex", flexDirection: "column" }}
        >
          {/* --- SWITCH CONTROLS (Responsive Toolbar) --- */}
          {adminOrCeo && (
            <Box
              sx={{
                mb: 1,
                p: 0.5,
                borderRadius: 3,
                bgcolor: (theme) => theme.palette.grey[100], // Light grey background
                display: "flex",
                alignItems: "center",
                // Mobile: Spread content (Label left, Switch right). Desktop: Compact right alignment
                justifyContent: { xs: "space-between", sm: "center" },
                width: "100%",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="body2"
                  color={!showAllEvents ? "primary.main" : "text.secondary"}
                  fontWeight={!showAllEvents ? "bold" : "regular"}
                  sx={{ transition: "all 0.3s" }}
                >
                  {t("admin.talentAcquisition.meetings.myInterviews")}
                </Typography>

                <Switch
                  checked={showAllEvents}
                  onChange={(e) => setShowAllEvents(e.target.checked)}
                  color="primary"
                  inputProps={{ "aria-label": "toggle view mode" }}
                />

                <Typography
                  variant="body2"
                  color={showAllEvents ? "primary.main" : "text.secondary"}
                  fontWeight={showAllEvents ? "bold" : "regular"}
                  sx={{ transition: "all 0.3s" }}
                >
                  {t("admin.talentAcquisition.meetings.teamInterviews")}
                </Typography>
              </Stack>
            </Box>
          )}

          {/* --- CALENDAR --- */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <InterviewCalendar
              initialEvents={visibleEvents}
              loading={meetingsLoading}
              localizer={localizer}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              employeedLoggedId={employee.id}
            />
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
};

MeetingsDialogWrapper.propTypes = {
  openMeetingsDialog: PropTypes.bool.isRequired,
  handleCloseMeetingsDialog: PropTypes.func.isRequired,
  adminOrCeo: PropTypes.bool.isRequired,
  employee: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
};

export default MeetingsDialogWrapper;
